import React, { useEffect } from 'react'
import { Button, Form, Input, Modal, notification, Typography } from 'antd'
import { z } from 'zod'

import { FlexRow } from '../../FlexboxReusables/FlexItems'
import { FormattedMessage } from 'react-intl'
import axios, { AxiosError, HttpStatusCode } from 'axios'
import { apiUrl, certificatesUrl } from '../../../globals'
import { GetCertificateResponse } from './ExamResults'

type AuthenticationModalProps = {
  examAttemptId: number
  showAuthenticateModal: boolean
  setShowAuthenticateModal: (show: boolean) => void
  firstName: string
  setFirstName: (firstName: string) => void
  lastName: string
  setLastName: (lastName: string) => void
  birthDate: string
  setBirthDate: (birthDate: string) => void
}

/**
 * @description Used to conditionally re-verify the user's data before being able to retrieve the certificate.
 * (Certificate contains personal data!)
 * 
 * Most likely only being used if calling from a different browser/machine than the one the exam was taken on.
 */
export const AuthenticationModal: React.FC<AuthenticationModalProps> = ({
  examAttemptId,
  showAuthenticateModal,
  setShowAuthenticateModal,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  birthDate,
  setBirthDate
}) => {
  useEffect(() => {
    const userData = localStorage.getItem('examUserData')

    if (userData) {
      const { firstName, lastName, birthDate } = JSON.parse(userData)

      setFirstName(firstName)
      setLastName(lastName)
      setBirthDate(birthDate)
    }
  }, [])

  const getExamCertificate = async () => { // TODO (DUPLICATE CODE): Refactor to use the same function as in ExamResults.tsx
    try {
      const pdfResponse = await axios.post<GetCertificateResponse>(`${apiUrl}/get-certificate`, {
        examAttemptId,
        firstName,
        lastName,
        birthDate
      })

      const { examAttemptYear, examAttemptMonth } = pdfResponse.data
      const downloadLink = `${certificatesUrl}/${examAttemptYear}/${examAttemptMonth}/${examAttemptId}/Certificate.pdf`

      window.location.href = downloadLink
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === HttpStatusCode.BadRequest) {
        notification.error({
          message: 'Ungültige Daten',
          description: 'Die eingegebenen Daten stimmen nicht mit den hinterlegten Daten überein.',
          placement: 'bottomRight',
        })

        // assume that the data is invalid and give the user the possibility to re-enter it
        localStorage.removeItem('examUserData')
        setShowAuthenticateModal(true)
      }
    }
  }

  const handleConfirmClick = async () => {
    const inputValidation = userInputSchema.safeParse({ firstName, lastName, birthDate })

    if (inputValidation.success) {
      await getExamCertificate()
    } else {
      notification.error({
        message: 'Validierungsfehler',
        description: (
          <ul>
            {inputValidation.error.errors.map((err, index) => (
              <li key={index}>{err.message}</li>
            ))}
          </ul>
        ),
        placement: 'bottomRight',
      })
    }
  }

  return (
    <Modal
      centered
      title="Authentifizierung erforderlich"
      onCancel={() => setShowAuthenticateModal(false)}
      open={showAuthenticateModal}
      footer={[
        <Button key="confirm" type="primary" onClick={handleConfirmClick}>
          Absenden
        </Button>
      ]}
    >
      {<>
        <FlexRow justifyContent='flex-start' margin='1.5em 0'>
          <Typography.Text>
              Bitte geben Sie Ihre Daten ein, um sich zu verifizieren.
          </Typography.Text>
        </FlexRow>
        <Form>
          <Form.Item label={<FormattedMessage id='generic.firstName' />} required>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Form.Item>
          <Form.Item label={<FormattedMessage id='generic.lastName' />} required>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Form.Item>
          <Form.Item label={<FormattedMessage id='generic.birthDate' />} required>
            <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
          </Form.Item>
        </Form>
      </>}
    </Modal>
  )
}

export default AuthenticationModal

export type GetExamCertificateRequest = {
  examAttemptId: number
  firstName: string
  lastName: string
  birthDate: Date
}

export const userInputSchema = z.object({
  firstName: z.string().min(2, "Der Vorname muss mindestens 2 Zeichen lang sein."),
  lastName: z.string().min(2, "Der Nachname muss mindestens 2 Zeichen lang sein."),
  birthDate: z.string()
    .refine(val => !isNaN(Date.parse(val)), {
      message: "Ungültiges Geburtsdatum. Bitte geben Sie das Datum im Format JJJJ.MM.TT an."
    })
    .transform(val => new Date(val)),
})
